import { computed, inject, Injectable } from '@angular/core';
import { UserService } from './user.service';
import { buildMenuForUser } from '../utils';

@Injectable({ providedIn: 'root' })
export class UserNavigationService {
  private readonly userService = inject(UserService);

  public readonly menu = computed(() => {
    const rawRoles = this.userService.user()?.mainMenuRoles ?? [];
    return buildMenuForUser(rawRoles);
  });

  public readonly allowedPaths = computed(() => {
    const rawRoles = this.userService.user()?.mainMenuRoles ?? [];

    const roots = rawRoles.filter((r) => !r.partOfId);
    const children = rawRoles.filter((r) => r.partOfId);

    roots.sort((a, b) => a.order - b.order);

    const resultPaths: string[] = [];

    for (const root of roots) {
      const myChildren = children
        .filter((child) => child.partOfId === root.id)
        .sort((a, b) => a.order - b.order);

      const hasChildren = myChildren.length > 0;

      if (!hasChildren && this.isValidPath(root.path)) {
        resultPaths.push(root.path);
      }

      for (const child of myChildren) {
        if (this.isValidPath(child.path)) {
          const fullPath = root.path ? `${root.path}/${child.path}` : child.path;
          resultPaths.push(fullPath);
        }
      }
    }

    return resultPaths;
  });

  public hasPath(url: string): boolean {
    const targetPath = this.normalize(url);
    const paths = this.allowedPaths();

    return !!paths.find((path) => {
      const normalizedP = this.normalize(path);

      if (targetPath === normalizedP) return true;
      if (targetPath.startsWith(normalizedP + '/')) return true;

      return targetPath.startsWith(normalizedP + '?');
    });
  }

  public getFirstPath(): string | undefined {
    return this.allowedPaths()[0];
  }

  private isValidPath(path: string | null | undefined): boolean {
    if (!path) return false;
    if (this.isExternal(path)) return false;

    return path.trim() !== '';
  }

  private isExternal(path: string): boolean {
    return !!path && /^https?:\/\//i.test(path);
  }

  private normalize(path: string): string {
    if (!path) return '';
    let clean = path.startsWith('/') ? path.slice(1) : path;
    clean = clean.split('?')[0];
    return clean.replace(/\/+$/, '');
  }
}
